import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { decodeEventLog, formatUnits, keccak256, type Address, type PublicClient, type WalletClient } from "viem";
import { useConnection, usePublicClient, useWalletClient } from "wagmi";
import { base } from "wagmi/chains";
import { botnetRegistryV2Abi } from "@botnet/shared/abi-v2";
import type { DeploymentReference, PublicV2Purchase } from "../lib/public-v2";
import { exactRegistryCreditWithdrawalTransaction, isDeadlineRefundCandidate, makeRegistryCreditWithdrawalIntent, parseRegistryCreditWithdrawalIntent,
  registryCreditWithdrawalKey, withRegistryCreditWithdrawalHash,
  type RegistryCreditWithdrawalIntent } from "../lib/registry-credit-withdrawal";

const QUEUED_PHASE = 2;
const REFUNDED_PHASE = 5;
const CONFIRMATIONS = 12n;
const TX_HASH = /^0x[0-9a-fA-F]{64}$/;

function sameAddress(a: unknown, b: string): boolean { return typeof a === "string" && a.toLowerCase() === b.toLowerCase(); }

function readSavedIntent(key: string, deployment: DeploymentReference, beneficiary: string): RegistryCreditWithdrawalIntent | null {
  try {
    const text = window.localStorage.getItem(key);
    if (!text) return null;
    const parsed = parseRegistryCreditWithdrawalIntent(JSON.parse(text) as unknown, deployment, beneficiary);
    if (!parsed) throw new Error();
    return parsed;
  } catch { throw new Error("saved withdrawal state unavailable"); }
}

function saveIntent(key: string, intent: RegistryCreditWithdrawalIntent): void {
  window.localStorage.setItem(key, JSON.stringify(intent));
}

function sameSavedIntent(key: string, deployment: DeploymentReference, beneficiary: string, expected: RegistryCreditWithdrawalIntent): boolean {
  const current = readSavedIntent(key, deployment, beneficiary);
  return !!current && JSON.stringify(current) === JSON.stringify(expected);
}

function clearSavedIntent(key: string, deployment: DeploymentReference, beneficiary: string, expected: RegistryCreditWithdrawalIntent): boolean {
  if (!sameSavedIntent(key, deployment, beneficiary, expected)) return false;
  window.localStorage.removeItem(key);
  return true;
}

export function RegistryCreditWithdrawal({ purchase, deployment }: { purchase: PublicV2Purchase; deployment: DeploymentReference }) {
  const connection = useConnection();
  const publicClient = usePublicClient({ chainId: base.id }) as PublicClient | undefined;
  const walletQuery = useWalletClient({ chainId: base.id, connector: connection.connector });
  const walletClient = walletQuery.data as WalletClient | undefined;
  const beneficiary = purchase.payer.toLowerCase() as Address;
  const key = useMemo(() => registryCreditWithdrawalKey(deployment, beneficiary), [deployment, beneficiary]);
  const connectedPayer = connection.status === "connected" && connection.chainId === base.id && sameAddress(connection.address, beneficiary);
  const matchingWallet = connectedPayer && walletClient?.chain?.id === base.id && sameAddress(walletClient.account?.address, beneficiary);
  const [credit, setCredit] = useState<bigint | null>(null);
  const [intent, setIntent] = useState<RegistryCreditWithdrawalIntent | null>(null);
  const [onchainPhase, setOnchainPhase] = useState<number | null>(null);
  const [onchainPrincipal, setOnchainPrincipal] = useState<bigint | null>(null);
  const [refundAvailable, setRefundAvailable] = useState(false);
  const [storageBlocked, setStorageBlocked] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("Checking the Registry purchase and credit…");
  const actionLockRef = useRef(false);
  const crossTabLockAvailable = typeof navigator !== "undefined" && typeof navigator.locks?.request === "function";

  useEffect(() => {
    setCredit(null); setIntent(null); setOnchainPhase(null); setOnchainPrincipal(null); setRefundAvailable(false);
    setStorageBlocked(false); setLoaded(false);
    if (!key) { setMessage("Registry refund recovery is unavailable for this deployment."); return; }
    try {
      const saved = readSavedIntent(key, deployment, beneficiary);
      setIntent(saved);
      setMessage(saved ? "A previous Registry transaction is unresolved. Check its status before another action." : "Checking the Registry purchase and credit…");
    } catch {
      setStorageBlocked(true);
      setMessage("Saved Registry transaction state could not be validated. Further Registry actions are disabled.");
    }
    setLoaded(true);
  }, [key, deployment, beneficiary]);

  const inspectDeployment = useCallback(async () => {
    if (!publicClient || await publicClient.getChainId() !== base.id || deployment.chainId !== base.id) throw new Error();
    const bytecode = await publicClient.getBytecode({ address: deployment.registryAddress });
    if (!bytecode || bytecode === "0x" || keccak256(bytecode) !== deployment.registryRuntimeCodeHash) throw new Error();
    return publicClient;
  }, [publicClient, deployment]);

  const readState = useCallback(async (client: PublicClient, agentId: string) => {
    const [rawPurchase, rawCredit, canRefund] = await Promise.all([
      client.readContract({ address: deployment.registryAddress, abi: botnetRegistryV2Abi, functionName: "purchase", args: [BigInt(agentId)] }),
      client.readContract({ address: deployment.registryAddress, abi: botnetRegistryV2Abi, functionName: "creditOf", args: [beneficiary] }),
      client.readContract({ address: deployment.registryAddress, abi: botnetRegistryV2Abi, functionName: "refundAvailable", args: [BigInt(agentId)] }),
    ]);
    const chainPurchase = rawPurchase as unknown as { exists: boolean; phase: number; payer: Address; principal: bigint };
    if (typeof chainPurchase.exists !== "boolean" || typeof chainPurchase.phase !== "number" || typeof chainPurchase.principal !== "bigint" ||
        !sameAddress(chainPurchase.payer, beneficiary) || typeof rawCredit !== "bigint" || typeof canRefund !== "boolean") throw new Error();
    return { chainPurchase, credit: rawCredit, canRefund };
  }, [deployment, beneficiary]);

  const refreshUnderLock = useCallback(async (saved: RegistryCreditWithdrawalIntent | null) => {
    if (!key) { setMessage("Registry refund recovery is unavailable for this deployment."); return; }
    try {
      const client = await inspectDeployment();
      const agentId = saved?.agentId ?? purchase.agentId;
      const state = await readState(client, agentId);
      setOnchainPhase(state.chainPurchase.phase);
      setOnchainPrincipal(state.chainPurchase.principal);
      setRefundAvailable(state.canRefund);
      setCredit(state.credit);
      if (saved) {
        setIntent(saved);
        if (!saved.transactionHash) {
          setMessage("A wallet request was started without a captured transaction hash. Its outcome is unresolved; no repeat transaction will be sent."); return;
        }
        const receipt = await client.getTransactionReceipt({ hash: saved.transactionHash }).catch(() => null);
        if (!receipt) { setMessage("The Registry transaction has no canonical receipt yet. No repeat transaction will be sent."); return; }
        const transaction = await client.getTransaction({ hash: saved.transactionHash }).catch(() => null);
        if (!transaction || transaction.hash !== saved.transactionHash || !exactRegistryCreditWithdrawalTransaction(transaction, saved) ||
            !sameAddress(receipt.to, deployment.registryAddress) || receipt.transactionHash !== saved.transactionHash ||
            receipt.blockNumber <= BigInt(saved.preparedBlockNumber) || transaction.blockNumber !== receipt.blockNumber || transaction.blockHash !== receipt.blockHash) {
          setMessage("Saved transaction evidence does not match the Registry, payer, and intended action. Further Registry actions remain blocked."); return;
        }
        const preparedAnchor = await client.getBlock({ blockNumber: BigInt(saved.preparedBlockNumber) }).catch(() => null);
        if (!preparedAnchor || preparedAnchor.hash !== saved.preparedBlockHash) {
          setMessage("The saved pre-dispatch block anchor is no longer canonical. The action remains unresolved for review."); return;
        }
        const canonical = await client.getBlock({ blockNumber: receipt.blockNumber }).catch(() => null);
        const head = await client.getBlockNumber().catch(() => 0n);
        if (!canonical || canonical.hash !== receipt.blockHash || head < receipt.blockNumber + CONFIRMATIONS) {
          setMessage("Registry receipt is awaiting 12 canonical confirmations. No repeat transaction will be sent."); return;
        }
        if (receipt.status === "reverted") {
          const unchanged = saved.kind === "refundAfterDeadline"
            ? state.chainPurchase.phase === QUEUED_PHASE && state.credit === BigInt(saved.startingCredit)
            : state.credit === BigInt(saved.amount);
          if (!unchanged) { setMessage("The reverted call is confirmed, but related on-chain state changed. Further actions remain blocked for review."); return; }
          if (!clearSavedIntent(key, deployment, beneficiary, saved)) { setMessage("Saved Registry intent changed during reconciliation. Further actions remain blocked."); return; }
          setIntent(null);
          setMessage("The Registry call reverted without changing its target state. You can recheck eligibility before retrying."); return;
        }
        let eventMatches = false;
        let withdrawnAmount: bigint | null = null;
        for (const log of receipt.logs) {
          if (!sameAddress(log.address, deployment.registryAddress)) continue;
          try {
            if (saved.kind === "refundAfterDeadline") {
              const decoded = decodeEventLog({ abi: botnetRegistryV2Abi, eventName: "RefundCredited", data: log.data, topics: log.topics, strict: true });
              if (BigInt(decoded.args.agentId) === BigInt(saved.agentId) && sameAddress(decoded.args.payer, beneficiary) && decoded.args.principal === BigInt(saved.amount)) eventMatches = true;
            } else {
              const decoded = decodeEventLog({ abi: botnetRegistryV2Abi, eventName: "CreditWithdrawn", data: log.data, topics: log.topics, strict: true });
              if (sameAddress(decoded.args.beneficiary, beneficiary) && sameAddress(decoded.args.to, beneficiary) && decoded.args.amount > 0n) {
                eventMatches = true; withdrawnAmount = decoded.args.amount;
              }
            }
          } catch { /* Ignore logs that do not decode as the selected Registry event. */ }
        }
        if (receipt.status !== "success" || !eventMatches || saved.kind === "refundAfterDeadline" && state.chainPurchase.phase !== REFUNDED_PHASE) {
          setMessage("Confirmed receipt lacks the expected Registry event or state transition. Further actions remain blocked."); return;
        }
        const after = await readState(client, saved.agentId);
        setOnchainPhase(after.chainPurchase.phase); setOnchainPrincipal(after.chainPurchase.principal);
        setRefundAvailable(after.canRefund); setCredit(after.credit);
        if (!clearSavedIntent(key, deployment, beneficiary, saved)) { setMessage("Saved Registry intent changed during reconciliation. Further actions remain blocked."); return; }
        setIntent(null);
        setMessage(saved.kind === "refundAfterDeadline"
          ? `Registry credited ${formatUnits(BigInt(saved.amount), 6)} USDC to the payer. Check the aggregate payer credit before withdrawing.`
          : `Registry confirmed withdrawal of ${formatUnits(withdrawnAmount ?? BigInt(saved.amount), 6)} USDC to the payer wallet.`);
        return;
      }
      if (state.chainPurchase.phase === QUEUED_PHASE && state.canRefund) setMessage("The queued purchase is past its Registry refund deadline. The original payer can sign refundAfterDeadline to close it and credit the Registry.");
      else if (state.chainPurchase.phase === REFUNDED_PHASE) setMessage(state.credit > 0n ? "The purchase is refunded and Registry credit is available to the original payer." : "The purchase is refunded; no Registry credit is currently available to the payer.");
      else if (state.chainPurchase.phase === QUEUED_PHASE) setMessage("The queued purchase is not yet eligible for a deadline refund.");
      else setMessage("The current on-chain purchase phase is outside this recovery flow.");
    } catch {
      setCredit(null); setOnchainPhase(null); setOnchainPrincipal(null); setRefundAvailable(false);
      setMessage("Registry deployment or on-chain purchase state could not be verified. Recovery is disabled.");
    }
  }, [key, inspectDeployment, deployment, beneficiary, purchase.agentId, readState]);

  const refresh = useCallback(async () => {
    if (!key) { setMessage("Registry refund recovery is unavailable for this deployment."); return; }
    if (!crossTabLockAvailable) { setMessage("This browser cannot provide an exclusive cross-tab Registry transaction lock. Registry actions are disabled."); return; }
    try {
      await navigator.locks.request(key, { mode: "exclusive", ifAvailable: true }, async (lock) => {
        if (!lock) { setMessage("Another tab is reconciling Registry recovery for this payer. Check status again after it finishes."); return; }
        try {
          const current = readSavedIntent(key, deployment, beneficiary);
          setIntent(current);
          await refreshUnderLock(current);
        }
        catch { setStorageBlocked(true); setMessage("Saved Registry transaction state could not be verified. Further actions remain blocked."); }
      });
    } catch { setStorageBlocked(true); setMessage("The cross-tab Registry reconciliation lock failed. Recovery is disabled."); }
  }, [key, crossTabLockAvailable, refreshUnderLock, deployment, beneficiary]);

  useEffect(() => { if (loaded) void refresh(); }, [loaded, refresh]);

  const runAction = useCallback(async (kind: RegistryCreditWithdrawalIntent["kind"]) => {
    if (!key || !matchingWallet || !walletClient || intent || storageBlocked || busy || onchainPhase === null) return;
    if (!crossTabLockAvailable) { setMessage("This browser cannot provide an exclusive cross-tab Registry transaction lock. Registry actions are disabled."); return; }
    if (actionLockRef.current) return;
    actionLockRef.current = true;
    setBusy(true);
    let shouldRefresh = false;
    let actionStage = "lock acquisition";
    try {
      await navigator.locks.request(key, { mode: "exclusive", ifAvailable: true }, async (lock) => {
        if (!lock) { setMessage("Another tab is handling Registry recovery for this payer. Check status before continuing."); return; }
        try {
          actionStage = "persisted intent check";
          // Recheck durable state only after acquiring the origin-wide payer lock.
          const alreadySaved = readSavedIntent(key, deployment, beneficiary);
          if (alreadySaved) { setIntent(alreadySaved); setMessage("Another tab or an earlier session already has a Registry action in progress. Reconcile it before continuing."); return; }
          actionStage = "on-chain preflight";
          const client = await inspectDeployment();
          const state = await readState(client, purchase.agentId);
          setCredit(state.credit); setOnchainPhase(state.chainPurchase.phase); setOnchainPrincipal(state.chainPurchase.principal); setRefundAvailable(state.canRefund);
          let amount: bigint;
          if (kind === "refundAfterDeadline") {
            if (!isDeadlineRefundCandidate(purchase, state.chainPurchase, state.canRefund)) throw new Error();
            amount = state.chainPurchase.principal;
          } else {
            if (state.chainPurchase.phase !== REFUNDED_PHASE || state.credit <= 0n) throw new Error();
            amount = state.credit;
          }
          const headNumber = await client.getBlockNumber();
          const headBlock = await client.getBlock({ blockNumber: headNumber });
          if (!headBlock.hash) throw new Error();
          const prepared = makeRegistryCreditWithdrawalIntent({ deployment, kind, agentId: purchase.agentId, beneficiary, amount,
            startingCredit: state.credit, preparedBlockNumber: headNumber, preparedBlockHash: headBlock.hash });
          actionStage = "persisting intent";
          saveIntent(key, prepared);
          setIntent(prepared);
          setMessage("Registry action intent saved. Confirm the exact Base Registry call in your wallet; status will be reconciled before another action is allowed.");
          actionStage = "wallet request";
          const hash = kind === "refundAfterDeadline"
            ? await walletClient.writeContract({ address: deployment.registryAddress, abi: botnetRegistryV2Abi,
                functionName: "refundAfterDeadline", args: [BigInt(purchase.agentId)], account: beneficiary, chain: undefined })
            : await walletClient.writeContract({ address: deployment.registryAddress, abi: botnetRegistryV2Abi,
                functionName: "withdrawCredit", args: [beneficiary, beneficiary], account: beneficiary, chain: undefined });
          if (!TX_HASH.test(hash)) throw new Error();
          const submitted = withRegistryCreditWithdrawalHash(prepared, hash);
          actionStage = "persisting transaction hash";
          if (!sameSavedIntent(key, deployment, beneficiary, prepared)) throw new Error();
          saveIntent(key, submitted); setIntent(submitted);
          setMessage("Registry transaction submitted. Checking its canonical receipt and exact event evidence…");
          shouldRefresh = true;
        } catch {
          setStorageBlocked(true);
          setMessage(`Registry action outcome is unresolved during ${actionStage}. The saved intent blocks another transaction until reconciliation.`);
        }
      });
    } catch {
      setStorageBlocked(true);
      setMessage("The cross-tab Registry transaction lock failed. Recovery is disabled until status is checked again.");
    } finally { setBusy(false); }
    actionLockRef.current = false;
    if (shouldRefresh) await refresh();
  }, [key, matchingWallet, walletClient, intent, storageBlocked, busy, onchainPhase, inspectDeployment, readState,
    purchase.agentId, purchase.phase, purchase.refundCredited, deployment, beneficiary, refresh, crossTabLockAvailable]);

  if (purchase.phase !== "queued" && purchase.phase !== "refunded") return null;
  const canRefund = onchainPhase === QUEUED_PHASE && refundAvailable && !intent && !storageBlocked && crossTabLockAvailable;
  const canWithdraw = onchainPhase === REFUNDED_PHASE && credit !== null && credit > 0n && !intent && !storageBlocked && crossTabLockAvailable;
  return <section className="panel panel-pad stack" aria-labelledby={`registry-credit-${purchase.agentId}`}>
    <h3 id={`registry-credit-${purchase.agentId}`}>Registry refund recovery</h3>
    <p className="small muted">A deadline refund closes the queued purchase and credits its principal in the Registry; it does not withdraw USDC. Registry credit is aggregated by payer across purchases. A later withdrawal sends all credit then present to the original payer wallet. These actions do not return any AgentBudgetVault balance.</p>
    <div><span className="label">Observed Registry credit for payer</span><div className="stat-value">{credit === null ? "Checking…" : `${formatUnits(credit, 6)} USDC`}</div></div>
    <div className="small muted">Payer and fixed recipient: <span className="technical-reference">{beneficiary}</span></div>
    {!connectedPayer && <p className="small">Connect the original payer wallet on Base to sign either Registry action.</p>}
    {connectedPayer && !matchingWallet && <p className="small">The Base wallet signer does not match the connected payer. Registry actions are disabled.</p>}
    {onchainPhase === QUEUED_PHASE && refundAvailable && onchainPrincipal !== null && <p className="small">Refund principal to be credited if confirmed: {formatUnits(onchainPrincipal, 6)} USDC. This permanently closes the queued purchase.</p>}
    <div className="row">
      {onchainPhase === QUEUED_PHASE && <button className="btn" disabled={!matchingWallet || !canRefund || busy} onClick={() => void runAction("refundAfterDeadline")}>
        {busy ? "Checking…" : "Sign deadline refund"}
      </button>}
      {onchainPhase === REFUNDED_PHASE && <button className="btn" disabled={!matchingWallet || !canWithdraw || busy} onClick={() => void runAction("withdrawCredit")}>
        {busy ? "Checking…" : "Withdraw all Registry credit to payer"}
      </button>}
      <button className="linkish" disabled={busy} onClick={() => void refresh()}>Check status</button>
    </div>
    <p role="status" aria-live="polite" className="small muted">{message}</p>
  </section>;
}
